import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiBarChart2, FiArrowRight, FiTrendingUp, FiPieChart,
  FiActivity, FiUsers
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const OrgAnalytics = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/organization/analytics" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <Link to="/docs/organization" className="hover:text-blue-600">Organization</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Analytics</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-cyan-100 dark:bg-cyan-900/30 rounded-xl mb-4">
              <FiBarChart2 className="w-6 h-6 text-cyan-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Organization Analytics</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Gain insights into your organization's productivity, project progress, and team performance.
            </p>
          </div>

          {/* Overview */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Analytics Dashboard</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                The analytics dashboard provides a comprehensive view of your organization's activities, 
                helping you make data-driven decisions and track progress.
              </p>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center">
                  <FiTrendingUp className="w-6 h-6 text-green-600 mx-auto mb-2" />
                  <h4 className="font-medium text-gray-900 dark:text-white text-sm">Productivity</h4>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center">
                  <FiPieChart className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                  <h4 className="font-medium text-gray-900 dark:text-white text-sm">Task Distribution</h4>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center">
                  <FiActivity className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                  <h4 className="font-medium text-gray-900 dark:text-white text-sm">Activity Trends</h4>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center">
                  <FiUsers className="w-6 h-6 text-orange-600 mx-auto mb-2" />
                  <h4 className="font-medium text-gray-900 dark:text-white text-sm">Team Performance</h4>
                </div>
              </div>
            </div>
          </section>

          {/* Available Reports */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Available Reports</h2>
            <div className="space-y-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Task Completion Report</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Track task completion rates across teams and departments. See trends over time.
                </p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Project Progress Report</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Monitor project milestones, deadlines, and overall progress status.
                </p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Team Workload Report</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  View task distribution and workload balance across team members.
                </p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Budget Utilization Report</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Track budget spending and utilization across departments.
                </p>
              </div>
            </div>
          </section>

          {/* Access Levels */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Analytics Access</h2>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
              <p className="text-gray-600 dark:text-gray-300 mb-4">Analytics access varies by role:</p>
              <ul className="space-y-2">
                <li className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg">
                  <span className="font-medium text-gray-900 dark:text-white">Organization Admin</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Full organization analytics</span>
                </li>
                <li className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg">
                  <span className="font-medium text-gray-900 dark:text-white">Department Manager</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Department-level analytics</span>
                </li>
                <li className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg">
                  <span className="font-medium text-gray-900 dark:text-white">Team Lead</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Team-level analytics</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Navigation */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Related Guides</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/features/analytics"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Feature: Analytics</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/organization/finance"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Finance & Budgets</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">Last updated: January 27, 2026</div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">Yes</button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">No</button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default OrgAnalytics;
